import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { ApplicationConfigService } from '../config/application-config.service';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Credential } from './auth.model';
import { AccountService } from './account.service';
import { RxStompEventAction, RxStompService } from '../socket/RxStompService.service';

type JwtToken = {
	token: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
	private _api_authenticate = this.applicationConfigService.getEndpointFor('api/authenticate');
	private _authenticated: boolean = false;

	/**
	 * Constructor
	 */
	constructor(
		private _httpClient: HttpClient,
		private localStorageService: LocalStorageService,
		private sessionStorageService: SessionStorageService,
		private applicationConfigService: ApplicationConfigService,
		private accountService: AccountService,
		private _rxStompService: RxStompService
	) {
		this._rxStompService.authService(this);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * getter for access token
	 */

	set accessToken(value: string) {
		if (this.localStorageService.retrieve('accessToken')) {
			this.localStorageService.store('accessToken', value);
		} else if (this.sessionStorageService.retrieve('accessToken')) {
			this.sessionStorageService.store('accessToken', value);
		}
		this._rxStompService.doEvent(RxStompEventAction.LOGIN);
	}

	get accessToken(): string {
		return (
			this.localStorageService.retrieve('accessToken') ??
			this.sessionStorageService.retrieve('accessToken') ??
			''
		);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Forgot password
	 *
	 * @param email
	 */
	forgotPassword(email: string): Observable<any> {
		return this._httpClient.post('api/auth/forgot-password', email);
	}

	/**
	 * Reset password
	 *
	 * @param password
	 */
	resetPassword(password: string): Observable<any> {
		return this._httpClient.post('api/auth/reset-password', password);
	}

	/**
	 * Sign in
	 *
	 * @param credentials
	 */
	signIn(credentials: Credential): Observable<any> {
		// Throw error, if the user is already logged in
		if (this._authenticated) {
			return throwError('User is already logged in.');
		}

		return this._httpClient.post<JwtToken>(this._api_authenticate, credentials).pipe(
			switchMap((response: JwtToken) => {
				const accessToken = response.token;

				this._rxStompService.doEvent(RxStompEventAction.LOGIN);

				// Set the authenticated flag to true
				this._authenticated = true;

				if (credentials.rememberMe) {
					this.localStorageService.store('accessToken', accessToken);
					this.sessionStorageService.clear('accessToken');
				} else {
					this.sessionStorageService.store('accessToken', accessToken);
					this.localStorageService.clear('accessToken');
				}

				// Store the user on the user service
				this.storeUser();

				// Return a new observable with the response
				return of(response);
			})
		);
	}

	/**
	 * Sign in using the access token
	 */
	signInUsingToken(): Observable<any> {
		// Sign in using the token
		return this._httpClient.get<any>(this._api_authenticate).pipe(
			catchError(() =>
				// Return false
				of(false)
			),
			switchMap((response: JwtToken) => {
				const accessToken = response.token;

				// Replace the access token with the new one if it's available on
				// the response object.
				//
				// This is an added optional step for better security. Once you sign
				// in using the token, you should generate a new one on the server
				// side and attach it to the response object. Then the following
				// piece of code can replace the token with the refreshed one.
				if (accessToken) {
					this.accessToken = accessToken;
				}

				// Set the authenticated flag to true
				this._authenticated = true;

				// Store the user on the user service
				this.storeUser();

				// Return true
				return of(true);
			})
		);
	}

	private storeUser() {
		this.accountService.identity(true).subscribe();
	}

	/**
	 * Sign out
	 */
	signOut(): Observable<any> {
		// Remove the access token from the local storage
		this.localStorageService.clear('accessToken');
		this.sessionStorageService.clear('accessToken');

		// Set the authenticated flag to false
		this._authenticated = false;

		this._rxStompService.doEvent(RxStompEventAction.LOGOUT);

		// Return the observable
		return of(true);
	}

	/**
	 * Sign up
	 *
	 * @param user
	 */
	signUp(user: {
		name: string;
		email: string;
		password: string;
		company: string;
	}): Observable<any> {
		return this._httpClient.post('api/auth/sign-up', user);
	}

	/**
	 * Unlock session
	 *
	 * @param credentials
	 */
	unlockSession(credentials: { email: string; password: string }): Observable<any> {
		return this._httpClient.post('api/auth/unlock-session', credentials);
	}

	/**
	 * Check the authentication status
	 */
	check(): Observable<boolean> {
		// Check if the user is logged in
		if (this._authenticated) {
			return of(true);
		}

		// Check the access token availability
		if (!this.accessToken) {
			return of(false);
		}

		// Check the access token expire date
		if (AuthUtils.isTokenExpired(this.accessToken)) {
			return of(false);
		}

		// If the access token exists, and it didn't expire, sign in using it
		return this.signInUsingToken();
	}
}
